import { countOf, formatCount } from '@/components/Agents/SendValidator/utils/SendText';
import { _isNil, _notNil } from '@/littledash';
import type { Finding, SendRunMetadata } from '@/model/Agent.model';

/**
 * The full brand for AI output, used once where the AI is introduced (the setup help text, the AI
 * notes subtitle). The model behind it is never named, so the customer does not see it change when
 * the model does.
 */
export const IN_VIVO_AI = 'Benchling In Vivo AI';

/**
 * The short form used everywhere else ("AI review", "AI notes"). Never "AI checks": the AI checks and
 * decides nothing, and "checks" are the validation rules.
 */
export const IN_VIVO_AI_SHORT = 'AI';

/** A count of AI notes in words, e.g. "1 AI note", "9 AI notes". */
export const aiNotesCount = (count: number): string =>
  countOf(count, `${IN_VIVO_AI_SHORT} note`, `${IN_VIVO_AI_SHORT} notes`);

/** The wire value marking a finding as AI output. Mapped to {@link IN_VIVO_AI}, never shown as-is. */
const AI_ORIGIN = 'llm';

/** Where a finding came from, as the server records it on every finding. */
type FindingOrigin = Pick<Finding, 'origin'>;

export const isAiFinding = (finding: FindingOrigin): boolean => finding.origin === AI_ORIGIN;

/** A label telling a reviewer whether Benchling In Vivo AI took part in a run. */
export interface AiUseLabel {
  key: 'with_ai' | 'without_ai' | 'review_failed';
  text: string;
}

/**
 * Keyed on `llm_used`, not `mode`: an AI-enabled run with nothing to ask reads as without AI review,
 * and one whose calls were attempted and failed still reads as with it. A review that did not
 * complete says so instead, since it can fail before any call is made, and "without AI review"
 * beside "the review didn't complete" would give two answers. Null when the run recorded no mode,
 * which only an engine run outside the agent does.
 */
export const aiUseLabelOf = (runMetadata?: SendRunMetadata | null): AiUseLabel | null => {
  if (_isNil(runMetadata)) {
    return null;
  }
  if (aiReviewOutcomeOf(runMetadata) === 'failed') {
    return { key: 'review_failed', text: `${IN_VIVO_AI_SHORT} review didn't complete` };
  }
  return runMetadata.llm_used
    ? { key: 'with_ai', text: `With ${IN_VIVO_AI_SHORT} review` }
    : { key: 'without_ai', text: `Without ${IN_VIVO_AI_SHORT} review` };
};

/** How Benchling In Vivo AI's review of a run went, as the report reads it. */
export type AiReviewOutcome = 'not_requested' | 'completed' | 'failed' | 'not_recorded';

/**
 * Reads whether the AI review completed. A model-free run never asked for one. A run that may have
 * asked but recorded no outcome, as results stored before the outcome was recorded do, reads as not
 * recorded rather than as either answer, so an older result never claims the review completed.
 */
export const aiReviewOutcomeOf = (runMetadata?: SendRunMetadata | null): AiReviewOutcome => {
  if (_isNil(runMetadata)) {
    return 'not_recorded';
  }
  if (runMetadata.mode === 'model_free') {
    return 'not_requested';
  }
  return runMetadata.enrichment_status ?? 'not_recorded';
};

/** What the report says when the AI review did not complete. */
export const AI_REVIEW_FAILED_TEXT = `${IN_VIVO_AI_SHORT} review didn't complete. The validation checks are unaffected, but some notes or suggestions may be missing.`;

/** What the report says when a run that may have used AI recorded no outcome for its review. */
export const AI_REVIEW_NOT_RECORDED_TEXT = `${IN_VIVO_AI_SHORT} review outcome not recorded for this run.`;

/** What the run header says when the run recorded no AI use at all. */
export const AI_USE_NOT_RECORDED_TEXT = `${IN_VIVO_AI_SHORT} review not recorded`;

/** Who suggested a finding's value, as the server records it on each one that has a suggestion. */
type SuggestionOf = Pick<Finding, 'expected_value' | 'suggestion_origin'>;

/**
 * Who a suggestion is from, under its value: the AI review, or the validator's own closest match.
 * Null for a finding with no suggestion, and for one stored before suggestions said who made them.
 */
export const suggestionOriginLabelOf = (finding: SuggestionOf): string | null => {
  if (_isNil(finding.expected_value) || _isNil(finding.suggestion_origin)) {
    return null;
  }
  return finding.suggestion_origin === AI_ORIGIN ? `From ${IN_VIVO_AI_SHORT} review` : 'Closest match';
};

/**
 * Whether any of these suggestions predates suggestions saying who made them, so a run-level caveat
 * is still the only way to say some may be the AI's.
 */
export const hasUnlabelledSuggestion = (findings: ReadonlyArray<SuggestionOf>): boolean =>
  findings.some((finding) => _notNil(finding.expected_value) && _isNil(finding.suggestion_origin));

/**
 * How far the AI review reached, when it did not reach every file with findings; null when it did, or
 * when the run recorded nothing. The run's AI review has a fixed budget, so a large bundle can be cut
 * short; the files it did not reach have no AI notes or suggestions, and their checks are unaffected.
 */
export const aiReviewCoverageTextOf = (runMetadata?: SendRunMetadata | null): string | null => {
  const coverage = runMetadata?.review_coverage;
  if (_isNil(coverage) || coverage.files_reviewed >= coverage.files_to_review) {
    return null;
  }
  const reached = `${IN_VIVO_AI_SHORT} review reached ${formatCount(coverage.files_reviewed)} of ${countOf(
    coverage.files_to_review,
    'file',
    'files'
  )} with findings`;
  const why = _isNil(coverage.limited_by) ? '' : `, the most this run's ${IN_VIVO_AI_SHORT} review covers`;
  return `${reached}${why}. The other files have no ${IN_VIVO_AI_SHORT} notes or suggestions. The validation checks are unaffected.`;
};
